import { getPinCounterLabel } from './getPinCounterLabel';
import { EIcon, IOffer } from '../../../repositories/dailyrent/entities/offer/Offer';
import { IFeatureProperties } from '../../../types/map';

export interface IPinViewModel {
  /** Цена в пилюле; null — пин не рисуется */
  price: number | null;
  /** Показывать префикс «от»: цена минимальная из нескольких */
  isPriceFrom: boolean;
  isViewed: boolean;
  isFavorite: boolean;
  /** Иконки объявлений в порядке первого появления, без повторов */
  icons: EIcon[];
  /** Пин объединяет несколько объявлений: у него каунтер, цена «от», своя вёрстка пилюли */
  isMultiPin: boolean;
  /** Количество объектов в пине - для мультипинов */
  counterLabel: string | null;
}

function getUniqueIcons(offers: IOffer[]): EIcon[] {
  const icons = offers.map(offer => offer.icon).filter((icon): icon is EIcon => Boolean(icon));

  return Array.from(new Set(icons));
}

/** Минимальная цена пина - «от» в пилюле и в шапке шторки мультипина; нулевые цены не считаются */
export function getMinPrice(offers: IOffer[]): number | null {
  const prices = offers.map(offer => offer.averagePricePerDay).filter((price): price is number => Boolean(price));

  return prices.length > 0 ? Math.min(...prices) : null;
}

/**
 * Наведённое в листинге объявление фичи по realtyId из properties.hoveredOfferId;
 * чужой id или его отсутствие — наведения нет.
 */
function getHoveredOffer({ offers, hoveredOfferId }: IFeatureProperties): IOffer | null {
  if (hoveredOfferId === undefined || hoveredOfferId === null) {
    return null;
  }

  return offers.find(offer => offer.realtyId === hoveredOfferId) ?? null;
}

/**
 * Данные пилюли пина по всем объявлениям фичи.
 *
 * У одиночного пина правила вырождаются в его единственное объявление,
 * у мультипина — агрегируют весь список (минимальная цена, избранное - хотя бы одно,
 * просмотренность - все объявления).
 *
 * При наведённом объявлении (см. getHoveredOffer) пин показывает его данные
 * вместо агрегата, каунтер и просмотренность пина при этом остаются.
 */
export function getPinViewModel(properties: IFeatureProperties): IPinViewModel {
  const { offers } = properties;
  const counterLabel = getPinCounterLabel(properties);
  const isMultiPin = counterLabel !== null;
  const hoveredOffer = getHoveredOffer(properties);
  // Просмотренность - свойство пина: новое объявление в просмотренном мультипине снова делает его непросмотренным
  const isViewed = offers.length > 0 && offers.every(offer => offer.isViewed);

  if (hoveredOffer && isMultiPin) {
    return {
      // нулевая цена, как и в агрегате, считается отсутствующей
      price: hoveredOffer.averagePricePerDay || null,
      isPriceFrom: false,
      isViewed,
      isFavorite: hoveredOffer.isFavorite,
      icons: hoveredOffer.icon ? [hoveredOffer.icon] : [],
      counterLabel,
      isMultiPin,
    };
  }

  const isFavorite = offers.some(offer => offer.isFavorite);

  return {
    price: getMinPrice(offers),
    // «от» и каунтер берутся из одного источника — они не могут разойтись
    isPriceFrom: isMultiPin,
    isViewed,
    isFavorite,
    // Контроль эксперимента не меняем: у одиночного пина избранное прячет иконки объявления,
    // как в мастере. У мультипина по макету показываются все иконки вместе с избранным.
    icons: !isMultiPin && isFavorite ? [] : getUniqueIcons(offers),
    counterLabel,
    isMultiPin,
  };
}
